<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import RichTextEditor from '@/js/core/components/parts/editor/RichTextEditor.vue';
import CommentMediaUpload from '@/js/core/components/parts/trays/CommentMediaUpload.vue';
import CommentMediaGallery from '@/js/core/components/parts/trays/CommentMediaGallery.vue';
import PersonNameLink from '@/js/core/components/parts/PersonNameLink.vue';
import PlayIcon from '@/js/core/components/icons/PlayIcon.vue';
import { formatCommentDate } from '@/js/core/utils/formatters';
import { useUserStore } from '@/js/core/stores/user-store';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { SUPPORTED_MEDIA_TYPES } from '@/js/core/constants/media-upload';

const TYPE_LABELS = {
  image: 'Image',
  audio: 'Audio',
  video: 'Video',
  pdf: 'PDF',
};

const TRIGGER_VERBS = {
  image: 'Enlarge image',
  audio: 'Play audio',
  video: 'Play video',
  pdf: 'Open PDF',
};

const props = defineProps({
  comment: {
    type: Object,
    required: true,
  },
  activeProfileUuid: {
    type: String,
    default: null,
  },
  personPanelId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['delete', 'update', 'person-click']);

const handlePersonClick = (uuid, buttonEl) => emit('person-click', uuid, buttonEl);

const userStore = useUserStore();
const timezoneStore = useTimezoneStore();

const isOwner = computed(() => {
  const currentUserId = userStore.id ? String(userStore.id) : '';
  const authorId = props.comment.author?.id;

  if (!currentUserId || authorId === null || authorId === undefined) return false;

  return String(authorId) === currentUserId;
});

const formattedCreatedAt = computed(() => {
  return formatCommentDate(props.comment.created_at, timezoneStore.effectiveTimezone);
});

const authorName = computed(() => {
  return props.comment.author?.name || 'Unknown';
});

const isEditing = ref(false);

const showActions = computed(
  () => (isOwner.value || userStore.canModerateComments) && !isEditing.value
);

const editorContent = ref('');
const editorRef = ref(null);
const editMediaRef = ref(null);
const editMedia = ref([]);
const editError = ref('');
const editErrorRef = ref(null);
const selectedMedia = ref(null);

const isSupportedMedia = item =>
  SUPPORTED_MEDIA_TYPES.includes(item.media_type) && Boolean(item.url);

const typeLabel = item => TYPE_LABELS[item.media_type] || 'File';

const rendersAsImage = item =>
  item.media_type === 'image' || (item.media_type === 'pdf' && Boolean(item.display?.url));

const tileLabel = item => {
  const verb = TRIGGER_VERBS[item.media_type] || 'Open media';
  const description = String(item.alt || '').trim();
  return description ? `${verb}: ${description}` : verb;
};

// The media fragment nudges Safari to paint the first frame; preload="metadata"
// alone leaves the preview blank there.
const videoPreviewSrc = item => `${item.url}#t=0.001`;

const openMedia = item => {
  if (isSupportedMedia(item)) {
    selectedMedia.value = item;
  }
};

const closeMedia = () => {
  selectedMedia.value = null;
};

const focusEditError = () => {
  nextTick(() => {
    editErrorRef.value?.focus();
  });
};

const handleEdit = async () => {
  editorContent.value = props.comment.content || '';
  editMedia.value = props.comment.media ? props.comment.media.map(m => ({ ...m })) : [];
  editError.value = '';
  isEditing.value = true;

  await nextTick();

  const editorEl = editorRef.value?.editorRef?.value;
  if (editorEl) {
    editorEl.focus();
    const range = document.createRange();
    range.selectNodeContents(editorEl);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }
};

const cancelEdit = () => {
  resetEdit();
};

const resetEdit = () => {
  isEditing.value = false;
  editorContent.value = '';
  editMedia.value = [];
  editError.value = '';
};

const saveEdit = () => {
  const content = editorContent.value?.trim();
  const hasContent = content && content !== '<br>';
  const hasMedia = editMedia.value.length > 0;

  if (!hasContent && !hasMedia) return;

  editError.value = '';

  if (hasMedia && editMediaRef.value?.validateMediaMetadata?.() === false) {
    editError.value =
      'Add the required description for each attachment and remove unsupported media.';
    focusEditError();
    return;
  }

  const updateData = {
    uuid: props.comment.uuid,
    content: hasContent ? content : '',
    media: hasMedia
      ? editMedia.value.map(({ id, alt, caption }) => ({
          id,
          alt: alt || '',
          caption: caption || '',
        }))
      : [],
    // The editor stays open until the server confirms; only success resets it,
    // so an unsupported item or a failed save never silently drops edits.
    onSuccess: () => resetEdit(),
    onError: message => {
      editError.value = message || 'Failed to update comment';
      focusEditError();
    },
  };

  emit('update', updateData);
};

const handleDelete = () => {
  if (confirm('Are you sure?')) {
    emit('delete', props.comment.uuid);
  }
};

onMounted(() => {
  timezoneStore.hydrateFromCurrentProfile();
});
</script>

<template>
  <article class="view-tray-modal__comment">
    <div class="view-tray-modal__comment-header">
      <div class="view-tray-modal__comment-meta">
        <div class="view-tray-modal__comment-author">
          <PersonNameLink
            :name="authorName"
            :profile-uuid="comment.author?.profile_uuid"
            :expanded="!!activeProfileUuid && activeProfileUuid === comment.author?.profile_uuid"
            :controls="personPanelId"
            @click="handlePersonClick"
          />
        </div>
        <div class="view-tray-modal__comment-date">
          {{ formattedCreatedAt }}
        </div>
      </div>
      <div v-if="showActions" class="view-tray-modal__comment-actions">
        <button
          v-if="isOwner"
          type="button"
          class="view-tray-modal__comment-btn--edit"
          @click="handleEdit"
        >
          Edit
        </button>
        <button type="button" class="view-tray-modal__comment-btn--delete" @click="handleDelete">
          Delete
        </button>
      </div>
    </div>
    <div v-if="!isEditing" class="view-tray-modal__comment-body">
      <div
        v-if="comment.content"
        class="view-tray-modal__comment-content"
        v-html="comment.content"
      />
      <div v-if="comment.media && comment.media.length" class="view-tray-modal__comment-media-list">
        <template v-for="item in comment.media" :key="item.id">
          <button
            v-if="isSupportedMedia(item)"
            type="button"
            class="view-tray-modal__comment-media view-tray-modal__comment-media--button"
            :class="`view-tray-modal__comment-media--${item.media_type}`"
            :aria-label="tileLabel(item)"
            @click="openMedia(item)"
          >
            <img
              v-if="rendersAsImage(item)"
              :alt="item.alt || ''"
              :src="item.display?.url || item.url"
              :srcset="item.display?.srcset || undefined"
              :sizes="
                item.display?.srcset
                  ? '(min-width: 1001px) 33vw, (min-width: 769px) 50vw, 100vw'
                  : undefined
              "
              :width="item.display?.width || undefined"
              :height="item.display?.height || undefined"
              class="view-tray-modal__comment-media-image"
              loading="lazy"
              decoding="async"
              @contextmenu.prevent
            />
            <span
              v-else-if="item.media_type === 'video'"
              class="view-tray-modal__comment-media-video"
            >
              <video
                :src="videoPreviewSrc(item)"
                class="view-tray-modal__comment-media-video-frame"
                preload="metadata"
                muted
                playsinline
                tabindex="-1"
                aria-hidden="true"
              ></video>
              <span class="view-tray-modal__comment-media-play" aria-hidden="true">
                <PlayIcon />
              </span>
            </span>
            <span v-else class="view-tray-modal__comment-media-badge">{{ typeLabel(item) }}</span>
            <span v-if="item.caption" class="view-tray-modal__comment-media-caption">
              {{ item.caption }}
            </span>
          </button>
          <div
            v-else
            class="view-tray-modal__comment-media view-tray-modal__comment-media--unsupported"
          >
            <span class="view-tray-modal__comment-media-badge">Unsupported media</span>
            <span v-if="item.alt" class="view-tray-modal__comment-media-description">
              {{ item.alt }}
            </span>
            <span v-if="item.caption" class="view-tray-modal__comment-media-caption">
              {{ item.caption }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <div v-else class="view-tray-modal__comment-edit-section">
      <h3 class="view-tray-modal__comment-heading">Comment</h3>
      <div class="view-tray-modal__comment-edit">
        <RichTextEditor ref="editorRef" v-model="editorContent" :placeholder="''" />

        <div class="view-tray-modal__comment-edit-media">
          <h3 class="view-tray-modal__comment-heading">Media</h3>
          <CommentMediaUpload ref="editMediaRef" v-model="editMedia" />
        </div>

        <div
          v-if="editError"
          ref="editErrorRef"
          tabindex="-1"
          class="view-tray-modal__submit-error"
          role="alert"
        >
          {{ editError }}
        </div>

        <div class="view-tray-modal__comment-edit-actions">
          <button
            class="view-tray-modal__comment-edit-save"
            type="button"
            :disabled="editMediaRef?.isUploading"
            @click="saveEdit"
          >
            Update
          </button>
          <button class="view-tray-modal__comment-edit-cancel" type="button" @click="cancelEdit">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <CommentMediaGallery v-if="selectedMedia" :media="selectedMedia" @close="closeMedia" />
  </article>
</template>
